import FilterAllCheckbox from "./FilterAllCheckbox";
import styles from "../FilterPanel.module.css";
import { CANTON_COLOR_MAP } from "../../../lib/constants";
import type { Filters, UpdateFilterFn, ToggleArrayFilterFn, Canton } from "../types";

type Props = {
  filters: Filters;
  allowedCantons: Canton[];
  otherCantons: Canton[];
  updateFilter: UpdateFilterFn;
  toggleArrayFilter: ToggleArrayFilterFn;
  isMobile: boolean;
  isActive: boolean;
};

const CantonFilters = ({
  filters,
  allowedCantons,
  otherCantons,
  updateFilter,
  toggleArrayFilter,
  isMobile,
  isActive,
}: Props) => {
  const total = allowedCantons.length + otherCantons.length;
  const allCantonNames = [...allowedCantons.map((c) => c.name), ...otherCantons.map((c) => c.name)];

  const renderCantonCheckbox = (canton: Canton) => {
    const color = CANTON_COLOR_MAP[canton.name as keyof typeof CANTON_COLOR_MAP] || "#999";
    return (
      <label key={canton.code} className={`${styles.itemLabel} ${styles.itemLabelHover}`}>
        <input
          type="checkbox"
          checked={filters.cantons.includes(canton.name)}
          onChange={() => toggleArrayFilter("cantons", canton.name)}
          className={`${styles.checkbox} ${styles.checkboxColored}`}
          style={{ ["--checkbox-bg-color" as any]: color }}
        />
        {canton.name} ({canton.code})
      </label>
    );
  };

  const renderCantonRadio = (canton: Canton) => (
    <label key={canton.code} className={`${styles.itemLabel} ${styles.itemLabelHover}`}>
      <input
        type="radio"
        name="canton-radio"
        checked={filters.cantons.length === 1 && filters.cantons[0] === canton.name}
        onChange={() => updateFilter("cantons", [canton.name])}
        className={styles.radio}
      />
      {canton.name} ({canton.code})
    </label>
  );

  const renderOtherOption = () => {
    const isOtherChecked = otherCantons.every((c) => filters.cantons.includes(c.name));
    const otherColor = CANTON_COLOR_MAP["Other"] || "#999";

    if (isActive) {
      return (
        <label className={`${styles.itemLabel} ${styles.itemLabelHover}`} style={{ width: "100%" }}>
          <input
            type="checkbox"
            checked={isOtherChecked}
            onChange={() => {
              if (isOtherChecked) {
                updateFilter(
                  "cantons",
                  filters.cantons.filter((n) => !otherCantons.some((c) => c.name === n))
                );
              } else {
                const toAdd = otherCantons
                  .map((c) => c.name)
                  .filter((n) => !filters.cantons.includes(n));
                updateFilter("cantons", [...filters.cantons, ...toAdd]);
              }
            }}
            className={`${styles.checkbox} ${styles.checkboxColored}`}
            style={{ ["--checkbox-bg-color" as any]: otherColor }}
          />
          Other
        </label>
      );
    }

    return (
      <label className={`${styles.itemLabel} ${styles.itemLabelHover}`} style={{ width: "100%" }}>
        <input
          type="radio"
          name="canton-radio"
          checked={
            otherCantons.length > 0 && otherCantons.every((c) => filters.cantons.includes(c.name))
          }
          onChange={() =>
            updateFilter(
              "cantons",
              otherCantons.map((c) => c.name)
            )
          }
          className={styles.radio}
        />
        Other
      </label>
    );
  };

  return (
    <div>
      <div className={styles.cantonsHeaderRow}>
        <FilterAllCheckbox
          selected={filters.cantons}
          total={total}
          onToggle={(selectAll) => updateFilter("cantons", selectAll ? allCantonNames : [])}
        />
        {isMobile && (
          <button
            type="button"
            className={styles.sectionReset}
            onClick={() => updateFilter("cantons", [])}
          >
            Reset
          </button>
        )}
      </div>
      <div className={styles.cantonsGrid}>
        {allowedCantons.map(isActive ? renderCantonCheckbox : renderCantonRadio)}
      </div>
      <div className={styles.cantonOtherRow}>{renderOtherOption()}</div>
    </div>
  );
};

export default CantonFilters;
